import { useEffect, useState } from 'react'
import { Webhook, Plus, Trash2, Play, Pause } from 'lucide-react'
import { api } from '@/lib/api'

interface Subscription {
  id: string
  name: string
  targetUrl: string
  events: string[]
  signingSecret: string
  active: boolean
  consecutiveFailures: number
  pausedAt: string | null
  lastDeliveryAt: string | null
  lastErrorMessage: string | null
  filterJson: unknown
  _count: { deliveries: number }
}

interface Delivery {
  id: string
  event: string
  status: string
  responseStatus: number | null
  attempt: number
  errorMessage: string | null
  createdAt: string
  completedAt: string | null
}

const KNOWN_EVENTS = [
  'TAG_ADDED',
  'TAG_REMOVED',
  'FORM_SUBMITTED',
  'CONVERSATION_OPENED',
  'MESSAGE_INBOUND',
  'MESSAGE_OUTBOUND',
  'OPPORTUNITY_STAGE_CHANGED',
  'REVIEW_RECEIVED_NEGATIVE',
] as const

export function WebhooksPage() {
  const [items, setItems] = useState<Subscription[]>([])
  const [selected, setSelected] = useState<Subscription | null>(null)
  const [deliveries, setDeliveries] = useState<Delivery[]>([])
  const [draftName, setDraftName] = useState('')
  const [draftUrl, setDraftUrl] = useState('https://')
  const [draftEvents, setDraftEvents] = useState<string[]>(['TAG_ADDED'])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function load() {
    const r = await api<{ items: Subscription[] }>('/v1/marketing/webhooks')
    setItems(r.items)
  }

  useEffect(() => {
    void load()
  }, [])

  async function pick(s: Subscription) {
    setSelected(s)
    try {
      const r = await api<{ items: Delivery[] }>(`/v1/marketing/webhooks/${s.id}/deliveries`)
      setDeliveries(r.items)
    } catch {
      setDeliveries([])
    }
  }

  async function create(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setErr(null)
    try {
      await api('/v1/marketing/webhooks', {
        method: 'POST',
        body: JSON.stringify({ name: draftName, targetUrl: draftUrl, events: draftEvents }),
      })
      setDraftName('')
      setDraftUrl('https://')
      await load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function resume(s: Subscription) {
    await api(`/v1/marketing/webhooks/${s.id}/resume`, { method: 'POST' })
    await load()
    if (selected?.id === s.id) pick(s)
  }

  async function pauseSub(s: Subscription) {
    await api(`/v1/marketing/webhooks/${s.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ active: false }),
    })
    await load()
  }

  async function remove(s: Subscription) {
    if (!confirm(`¿Eliminar el webhook "${s.name}"?`)) return
    await api(`/v1/marketing/webhooks/${s.id}`, { method: 'DELETE' })
    if (selected?.id === s.id) {
      setSelected(null)
      setDeliveries([])
    }
    await load()
  }

  return (
    <div className="p-6 max-w-6xl">
      <header className="mb-4">
        <h1 className="text-lg font-medium tracking-tight flex items-center gap-2">
          <Webhook className="size-5 text-blueprint-700" />
          Webhooks · outbound
        </h1>
        <p className="text-xs font-mono text-ink-700/60">
          eventos de dominio JR2 → externos (n8n, Zapier, listeners propios) ·
          HMAC-SHA256 + anti-loop con <span className="font-medium">X-JR2-Origin</span> ·
          auto-pause tras 5 fallos
        </p>
      </header>

      <form onSubmit={(e) => void create(e)} className="bp-card p-3 mb-4 grid grid-cols-12 gap-2">
        <input
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          placeholder="nombre"
          required
          className="col-span-3 border border-ink-900/15 rounded px-2 py-1.5 text-sm"
        />
        <input
          value={draftUrl}
          onChange={(e) => setDraftUrl(e.target.value)}
          placeholder="https://target.url/webhook"
          required
          type="url"
          className="col-span-5 border border-ink-900/15 rounded px-2 py-1.5 text-sm font-mono"
        />
        <select
          multiple
          value={draftEvents}
          onChange={(e) =>
            setDraftEvents(Array.from(e.target.selectedOptions).map((o) => o.value))
          }
          className="col-span-3 border border-ink-900/15 rounded px-1 py-1 text-xs font-mono bg-white"
          size={3}
        >
          {KNOWN_EVENTS.map((ev) => (
            <option key={ev} value={ev}>
              {ev}
            </option>
          ))}
        </select>
        <button
          type="submit"
          className="bp-btn-primary col-span-1 justify-center"
          disabled={busy || !draftName.trim() || draftEvents.length === 0}
        >
          <Plus className="size-3" />
        </button>
      </form>
      {err && (
        <div className="bp-card p-2 mb-3 text-xs font-mono text-red-700 bg-red-50 border-red-200">
          {err}
        </div>
      )}

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-7 bp-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-alt text-xs font-mono uppercase tracking-wider text-ink-700/60">
              <tr>
                <th className="px-3 py-2 text-left">Nombre</th>
                <th className="px-3 py-2 text-left">Eventos</th>
                <th className="px-3 py-2 text-right">Fallos</th>
                <th className="px-3 py-2 text-right">Entregas</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => {
                const isPaused = s.pausedAt !== null
                return (
                  <tr
                    key={s.id}
                    onClick={() => void pick(s)}
                    className={[
                      'border-t border-ink-900/10 cursor-pointer hover:bg-surface-alt/50',
                      selected?.id === s.id ? 'bg-blueprint-50' : '',
                    ].join(' ')}
                  >
                    <td className="px-3 py-2">
                      <div className="font-medium flex items-center gap-2">
                        {s.name}
                        {isPaused && (
                          <span className="bp-pill bg-red-50 text-red-700">PAUSED</span>
                        )}
                        {!s.active && !isPaused && (
                          <span className="bp-pill bg-ink-900/10 text-ink-700/60">OFF</span>
                        )}
                      </div>
                      <div className="text-[10px] font-mono text-ink-700/60 truncate max-w-[300px]">
                        {s.targetUrl}
                      </div>
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-ink-700">
                      {s.events.join(', ')}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-ink-700">
                      {s.consecutiveFailures > 0 ? (
                        <span className="text-red-700">{s.consecutiveFailures}</span>
                      ) : (
                        '0'
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{s._count.deliveries}</td>
                    <td className="px-3 py-2 text-right">
                      {isPaused ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            void resume(s)
                          }}
                          className="bp-btn"
                          title="Resume"
                        >
                          <Play className="size-3" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            void pauseSub(s)
                          }}
                          className="bp-btn"
                          title="Pausar"
                        >
                          <Pause className="size-3" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          void remove(s)
                        }}
                        className="bp-btn text-red-700 ml-1"
                      >
                        <Trash2 className="size-3" />
                      </button>
                    </td>
                  </tr>
                )
              })}
              {items.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-xs text-ink-700/50">
                    Sin subscripciones.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="col-span-5">
          {selected ? (
            <div className="bp-card overflow-hidden">
              <div className="px-3 py-2 border-b border-ink-900/10 bg-surface-alt">
                <div className="text-xs font-mono uppercase tracking-wider text-ink-700/60">
                  últimas entregas · {selected.name}
                </div>
                {selected.lastErrorMessage && (
                  <div className="text-[10px] font-mono text-red-700 mt-1 truncate">
                    error: {selected.lastErrorMessage}
                  </div>
                )}
              </div>
              <ul className="divide-y divide-ink-900/5 max-h-[500px] overflow-auto">
                {deliveries.map((d) => (
                  <li key={d.id} className="px-3 py-2">
                    <div className="flex items-center gap-2 text-xs">
                      <span
                        className={[
                          'bp-pill',
                          d.status === 'SUCCESS'
                            ? 'bg-emerald-50 text-emerald-700'
                            : d.status === 'FAILED'
                              ? 'bg-red-50 text-red-700'
                              : 'bg-amber-50 text-amber-700',
                        ].join(' ')}
                      >
                        {d.status}
                      </span>
                      <span className="font-mono text-ink-700">{d.event}</span>
                      {d.responseStatus && (
                        <span className="font-mono text-ink-700/50">
                          {d.responseStatus}
                        </span>
                      )}
                      <span className="ml-auto text-[10px] font-mono text-ink-700/50">
                        {new Date(d.createdAt).toLocaleTimeString('es-ES')}
                      </span>
                    </div>
                    {d.errorMessage && (
                      <div className="mt-0.5 text-[10px] font-mono text-red-700 truncate">
                        {d.errorMessage}
                      </div>
                    )}
                  </li>
                ))}
                {deliveries.length === 0 && (
                  <li className="px-3 py-6 text-center text-xs text-ink-700/50">
                    sin entregas
                  </li>
                )}
              </ul>
            </div>
          ) : (
            <div className="bp-card p-6 text-center text-xs text-ink-700/50">
              Selecciona una subscripción para ver sus entregas.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
